// frontend/src/pages/Register.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faLock,
  faPhone,
  faMapMarkerAlt,
  faTractor,
  faBuilding,
  faArrowRight,
  faSpinner,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../components/UI/Button";

const Register = () => {
  const [userType, setUserType] = useState("customer");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    address: { street: "", city: "", district: "" },
    farmDetails: {
      farmName: "",
      farmSize: "small",
      livestockType: [],
      animalCount: 0,
      yearsInOperation: 0,
    },
    preferredPaymentMethod: "cash_on_delivery",
    companyName: "",
    registrationNumber: "",
    businessType: "manufacturer",
    productCategories: [],
    supplierAddress: {
      street: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    },
    supplierContact: { name: "", title: "", phone: "", whatsapp: "" },
    shippingTerms: { incoterms: "FOB", portOfLoading: "", leadTime: 30 },
    documents: {
      certificateOfAnalysis: false,
      certificateOfOrigin: false,
      phytosanitary: false,
      fumigation: false,
      halal: false,
    },
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const livestockOptions = [
    "Cattle",
    "Poultry",
    "Goats",
    "Sheep",
    "Pigs",
    "Mixed",
  ];
  const businessTypes = [
    { value: "manufacturer", label: "Manufacturer" },
    { value: "distributor", label: "Distributor" },
    { value: "trader", label: "Trader" },
    { value: "broker", label: "Broker" },
  ];
  const productCategoryOptions = [
    "rumen-bypass-fat",
    "mineral-blocks",
    "protein-concentrates",
    "feed-supplements",
    "premixes",
    "veterinary-products",
  ];
  const incotermsOptions = ["FOB", "CIF", "EXW", "DAP", "DDP"];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setError("");
  };

  const handleLivestockToggle = (type) => {
    setFormData((prev) => {
      const currentTypes = prev.farmDetails.livestockType;
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter((t) => t !== type)
        : [...currentTypes, type];
      return {
        ...prev,
        farmDetails: { ...prev.farmDetails, livestockType: newTypes },
      };
    });
  };

  const handleCategoryToggle = (category) => {
    setFormData((prev) => {
      const current = prev.productCategories;
      const newCategories = current.includes(category)
        ? current.filter((c) => c !== category)
        : [...current, category];
      return { ...prev, productCategories: newCategories };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    // Validation for each user type
    if (userType === "customer" && !formData.name) {
      setError("Full name is required");
      return;
    }
    if (userType === "farmer") {
      if (!formData.farmDetails.farmName) {
        setError("Farm name is required for farmer registration");
        return;
      }
      if (formData.farmDetails.livestockType.length === 0) {
        setError("Please select at least one livestock type");
        return;
      }
    }
    if (userType === "supplier") {
      if (!formData.companyName) {
        setError("Company name is required for supplier registration");
        return;
      }
      if (!formData.registrationNumber) {
        setError("Registration number is required");
        return;
      }
      if (!formData.supplierContact.name) {
        setError("Contact person name is required");
        return;
      }
      if (!formData.supplierAddress.country) {
        setError("Country is required for supplier address");
        return;
      }
      if (formData.productCategories.length === 0) {
        setError("Please select at least one product category");
        return;
      }
    }

    const registrationData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      phone: formData.phone || "",
      userType: userType,
      role: userType === "supplier" ? "supplier" : userType,
    };

    // Add address for customers and farmers
    if (
      formData.address.street ||
      formData.address.city ||
      formData.address.district
    ) {
      registrationData.address = formData.address;
    }

    if (userType === "farmer") {
      registrationData.farmDetails = {
        farmName: formData.farmDetails.farmName,
        farmSize: formData.farmDetails.farmSize,
        livestockType: formData.farmDetails.livestockType,
        animalCount: parseInt(formData.farmDetails.animalCount) || 0,
        yearsInOperation: parseInt(formData.farmDetails.yearsInOperation) || 0,
      };
      registrationData.preferredPaymentMethod = formData.preferredPaymentMethod;
      registrationData.bulkPurchaseEnabled = true;
      registrationData.creditLimit =
        formData.farmDetails.farmSize === "large" ? 5000 : 1000;
    }

    if (userType === "supplier") {
      registrationData.supplierData = {
        companyName: formData.companyName,
        registrationNumber: formData.registrationNumber,
        businessType: formData.businessType,
        productCategories: formData.productCategories,
        address: formData.supplierAddress,
        contactPerson: {
          name: formData.supplierContact.name,
          title: formData.supplierContact.title,
          phone: formData.supplierContact.phone || formData.phone,
          whatsapp: formData.supplierContact.whatsapp,
        },
        shippingTerms: {
          incoterms: formData.shippingTerms.incoterms,
          portOfLoading: formData.shippingTerms.portOfLoading,
          leadTime: parseInt(formData.shippingTerms.leadTime) || 30,
        },
        documents: formData.documents,
        isApproved: false,
      };
    }

    setLoading(true);
    try {
      await register(registrationData);
      navigate("/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.message || "Registration failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="auth-page"
    >
      <div className="auth-container" style={{ maxWidth: "700px" }}>
        <div className="auth-header">
          <FontAwesomeIcon icon={faUser} className="auth-icon" />
          <h1>Create an Account</h1>
          <p>Join Kenali Enterprise today</p>
        </div>

        {error && (
          <div className="auth-error">
            <FontAwesomeIcon icon={faLock} /> {error}
          </div>
        )}

        {/* User Type Selector */}
        <div className="user-type-selector">
          <button
            type="button"
            onClick={() => setUserType("customer")}
            className={`user-type-btn ${userType === "customer" ? "active" : ""}`}
          >
            <span className="user-type-icon">🛒</span>
            <span className="user-type-label">Customer</span>
          </button>
          <button
            type="button"
            onClick={() => setUserType("farmer")}
            className={`user-type-btn ${userType === "farmer" ? "active" : ""}`}
          >
            <span className="user-type-icon">🌾</span>
            <span className="user-type-label">Farmer</span>
          </button>
          <button
            type="button"
            onClick={() => setUserType("supplier")}
            className={`user-type-btn ${userType === "supplier" ? "active" : ""}`}
          >
            <span className="user-type-icon">🏢</span>
            <span className="user-type-label">Supplier</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Basic Information */}
          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faUser} /> Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faEnvelope} /> Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="you@example.com"
            />
          </div>

          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faPhone} /> Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+232 76 123456"
            />
          </div>

          {/* Address for Customers & Farmers */}
          {(userType === "customer" || userType === "farmer") && (
            <>
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Street Address
                </label>
                <input
                  type="text"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>District</label>
                  <input
                    type="text"
                    name="address.district"
                    value={formData.address.district}
                    onChange={handleChange}
                    placeholder="District"
                  />
                </div>
              </div>
            </>
          )}

          {/* Farmer Specific */}
          {userType === "farmer" && (
            <div className="auth-section">
              <h3 className="auth-section-title">
                <FontAwesomeIcon icon={faTractor} /> Farm Details
              </h3>

              <div className="form-group">
                <label>Farm/Business Name *</label>
                <input
                  type="text"
                  name="farmDetails.farmName"
                  value={formData.farmDetails.farmName}
                  onChange={handleChange}
                  required
                  placeholder="Enter farm name"
                />
              </div>

              <div className="form-group">
                <label>Farm Size</label>
                <select
                  name="farmDetails.farmSize"
                  value={formData.farmDetails.farmSize}
                  onChange={handleChange}
                >
                  <option value="small">Small (1-50 animals)</option>
                  <option value="medium">Medium (51-200 animals)</option>
                  <option value="large">Large (200+ animals)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Livestock Type *</label>
                <div className="checkbox-group">
                  {livestockOptions.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleLivestockToggle(type)}
                      className={`checkbox-btn ${
                        formData.farmDetails.livestockType.includes(type)
                          ? "active"
                          : ""
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Animals</label>
                  <input
                    type="number"
                    name="farmDetails.animalCount"
                    value={formData.farmDetails.animalCount}
                    onChange={handleChange}
                    min="0"
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Years in Operation</label>
                  <input
                    type="number"
                    name="farmDetails.yearsInOperation"
                    value={formData.farmDetails.yearsInOperation}
                    onChange={handleChange}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Preferred Payment Method</label>
                <select
                  name="preferredPaymentMethod"
                  value={formData.preferredPaymentMethod}
                  onChange={handleChange}
                >
                  <option value="cash_on_delivery">Cash on Delivery</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="credit">Credit (30 days)</option>
                </select>
              </div>
            </div>
          )}

          {/* Supplier Specific */}
          {userType === "supplier" && (
            <div className="auth-section">
              <h3 className="auth-section-title">
                <FontAwesomeIcon icon={faBuilding} /> Company Information
              </h3>

              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  placeholder="Company name"
                />
              </div>

              <div className="form-group">
                <label>Registration Number *</label>
                <input
                  type="text"
                  name="registrationNumber"
                  value={formData.registrationNumber}
                  onChange={handleChange}
                  required
                  placeholder="Registration number"
                />
              </div>

              <div className="form-group">
                <label>Business Type</label>
                <select
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                >
                  {businessTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <h4 className="sub-section-title">Contact Person</h4>

              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  name="supplierContact.name"
                  value={formData.supplierContact.name}
                  onChange={handleChange}
                  required
                  placeholder="Contact person name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Title</label>
                  <input
                    type="text"
                    name="supplierContact.title"
                    value={formData.supplierContact.title}
                    onChange={handleChange}
                    placeholder="Job title"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="supplierContact.phone"
                    value={formData.supplierContact.phone}
                    onChange={handleChange}
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>WhatsApp Number</label>
                <input
                  type="tel"
                  name="supplierContact.whatsapp"
                  value={formData.supplierContact.whatsapp}
                  onChange={handleChange}
                  placeholder="WhatsApp number"
                />
              </div>

              <h4 className="sub-section-title">Address</h4>

              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  name="supplierAddress.street"
                  value={formData.supplierAddress.street}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="supplierAddress.city"
                    value={formData.supplierAddress.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="supplierAddress.state"
                    value={formData.supplierAddress.state}
                    onChange={handleChange}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="supplierAddress.country"
                    value={formData.supplierAddress.country}
                    onChange={handleChange}
                    required
                    placeholder="Country"
                  />
                </div>
                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    name="supplierAddress.postalCode"
                    value={formData.supplierAddress.postalCode}
                    onChange={handleChange}
                    placeholder="Postal code"
                  />
                </div>
              </div>

              <h4 className="sub-section-title">Product Categories</h4>

              <div className="form-group">
                <label>Select Categories *</label>
                <div className="checkbox-group">
                  {productCategoryOptions.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => handleCategoryToggle(category)}
                      className={`checkbox-btn ${
                        formData.productCategories.includes(category)
                          ? "active"
                          : ""
                      }`}
                    >
                      {category.replace(/-/g, " ")}
                    </button>
                  ))}
                </div>
              </div>

              <h4 className="sub-section-title">Shipping & Logistics</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Incoterms</label>
                  <select
                    name="shippingTerms.incoterms"
                    value={formData.shippingTerms.incoterms}
                    onChange={handleChange}
                  >
                    {incotermsOptions.map((term) => (
                      <option key={term} value={term}>
                        {term}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Port of Loading</label>
                  <input
                    type="text"
                    name="shippingTerms.portOfLoading"
                    value={formData.shippingTerms.portOfLoading}
                    onChange={handleChange}
                    placeholder="Port of loading"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Lead Time (days)</label>
                <input
                  type="number"
                  name="shippingTerms.leadTime"
                  value={formData.shippingTerms.leadTime}
                  onChange={handleChange}
                  min="1"
                  placeholder="30"
                />
              </div>

              <h4 className="sub-section-title">Documents Available</h4>

              <div className="checkbox-grid">
                {[
                  {
                    key: "certificateOfAnalysis",
                    label: "Certificate of Analysis",
                  },
                  {
                    key: "certificateOfOrigin",
                    label: "Certificate of Origin",
                  },
                  { key: "phytosanitary", label: "Phytosanitary" },
                  { key: "fumigation", label: "Fumigation" },
                  { key: "halal", label: "Halal" },
                ].map((doc) => (
                  <label key={doc.key} className="checkbox-label">
                    <input
                      type="checkbox"
                      name={`documents.${doc.key}`}
                      checked={formData.documents[doc.key]}
                      onChange={handleChange}
                    />
                    {doc.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Password */}
          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faLock} /> Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              placeholder="Min 6 characters"
            />
          </div>

          <div className="form-group">
            <label>
              <FontAwesomeIcon icon={faLock} /> Confirm Password *
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength="6"
              placeholder="Confirm your password"
            />
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Creating Account...
              </>
            ) : (
              <>
                Register as{" "}
                {userType.charAt(0).toUpperCase() + userType.slice(1)}
                <FontAwesomeIcon icon={faArrowRight} />
              </>
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account? <Link to="/login">Login here</Link>
          </p>
        </div>

        <style>
          {`
          .auth-page {
            min-height: calc(100vh - 120px);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1.5rem;
            background: #f8f8f8;
          }
          @media (min-width: 768px) {
            .auth-page {
              padding: 2rem;
            }
          }

          .auth-container {
            background: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            max-width: 700px;
            width: 100%;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          }
          @media (min-width: 768px) {
            .auth-container {
              padding: 2.5rem;
              border-radius: 16px;
            }
          }

          .auth-icon {
            font-size: 2.5rem;
            color: #d9a321;
            margin-bottom: 0.5rem;
          }
          .auth-header h1 {
            color: #006b2e;
            font-size: 1.5rem;
            margin-bottom: 0.25rem;
          }
          .auth-header p {
            color: #5a5a5a;
            font-size: 0.875rem;
          }

          .auth-error {
            background: #fee2e2;
            color: #dc2626;
            padding: 0.625rem 0.875rem;
            border-radius: 8px;
            margin-bottom: 0.75rem;
            font-size: 0.813rem;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }

          .user-type-selector {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
            justify-content: center;
            margin-bottom: 1.5rem;
          }
          .user-type-btn {
            padding: 0.5rem 1rem;
            border: 2px solid #e5e5e5;
            border-radius: 8px;
            background: #ffffff;
            cursor: pointer;
            transition: all 0.3s ease;
            flex: 1;
            min-width: 80px;
            text-align: center;
          }
          .user-type-btn:hover {
            border-color: #d9a321;
          }
          .user-type-btn.active {
            border-color: #d9a321;
            background: rgba(217, 163, 33, 0.1);
          }
          .user-type-icon {
            display: block;
            font-size: 1.25rem;
            margin-bottom: 0.125rem;
          }
          .user-type-label {
            font-size: 0.65rem;
            font-weight: 600;
            color: #1f1f1f;
          }

          .auth-form .form-group {
            margin-bottom: 1rem;
          }
          .auth-form .form-group label {
            display: block;
            margin-bottom: 0.25rem;
            font-weight: 500;
            font-size: 0.813rem;
            color: #1f1f1f;
          }
          .auth-form .form-group label svg {
            margin-right: 0.5rem;
            color: #d9a321;
          }
          .auth-form .form-group input,
          .auth-form .form-group select {
            width: 100%;
            padding: 0.625rem 0.75rem;
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            font-size: 0.875rem;
            transition: border-color 0.3s ease;
            background: #ffffff;
            color: #1f1f1f;
          }
          .auth-form .form-group input:focus,
          .auth-form .form-group select:focus {
            outline: none;
            border-color: #d9a321;
            box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem;
          }
          @media (max-width: 576px) {
            .form-row {
              grid-template-columns: 1fr;
            }
          }

          .auth-section {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #e5e5e5;
          }
          .auth-section-title {
            font-size: 1rem;
            color: #006b2e;
            margin-bottom: 1rem;
          }
          .auth-section-title svg {
            margin-right: 0.5rem;
          }
          .sub-section-title {
            font-size: 0.938rem;
            color: #006b2e;
            margin: 1rem 0 0.5rem;
          }

          .checkbox-group {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          .checkbox-btn {
            padding: 0.4rem 0.875rem;
            border-radius: 20px;
            border: 1px solid #e5e5e5;
            background: #ffffff;
            cursor: pointer;
            font-size: 0.813rem;
            transition: all 0.3s ease;
          }
          .checkbox-btn:hover {
            border-color: #d9a321;
          }
          .checkbox-btn.active {
            border-color: #d9a321;
            background: #d9a321;
            color: #ffffff;
          }

          .checkbox-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
          }
          @media (max-width: 576px) {
            .checkbox-grid {
              grid-template-columns: 1fr;
            }
          }
          .checkbox-label {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
            font-size: 0.875rem;
          }
          .checkbox-label input[type="checkbox"] {
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #d9a321;
          }

          .auth-submit {
            width: 100%;
            padding: 0.625rem;
            background: #d9a321;
            color: #ffffff;
            border: none;
            border-radius: 8px;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            min-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            margin-top: 0.5rem;
          }
          .auth-submit:hover:not(:disabled) {
            background: #b8860b;
            transform: translateY(-2px);
          }
          .auth-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .auth-footer {
            text-align: center;
            margin-top: 1.25rem;
            padding-top: 1.25rem;
            border-top: 1px solid #e5e5e5;
          }
          .auth-footer a {
            color: #d9a321;
            text-decoration: none;
            font-weight: 500;
          }
          .auth-footer a:hover {
            text-decoration: underline;
          }
        `}
        </style>
      </div>
    </motion.div>
  );
};

export default Register;
